import { useEffect, useState } from "react";
import { useApi } from "../../hooks/ApiProvider/ApiProvider";
import { useSearchParams } from "react-router-dom";

const View = ({...props}) => {
  const api = useApi();
  const [searchParams] = useSearchParams();
  const [boardData, setBoardData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  const handleDeleteBoard = async () => {
    if (!boardData?.id) return;
    
    try {
      const result = await api.deleteBoard(boardData.id);
      if (result.success !== false) {
        alert('게시글이 삭제되었습니다.');
        props?.actChage('list');
      } else {
        alert('게시글 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('게시글 삭제 오류:', error);
      alert('게시글 삭제 중 오류가 발생했습니다.');
    }
  };
  
  useEffect(() => {
    const getBoardDetail = async () => {
      const boardId = searchParams.get('id');
      if (!boardId) return;
      
      setLoading(true);
      try {
        const result = await api.getBoard(parseInt(boardId));
        console.log('게시글 상세 조회 응답:', result);
        
        // API 응답이 직접 게시글 데이터인 경우
        if (result && result.id) {
          setBoardData(result);
        } 
        // API 응답이 {success: true, board: {...}} 구조인 경우
        else if (result.success && result.board) {
          setBoardData(result.board);
        } 
        // 기타 경우
        else {
          alert('게시글을 불러올 수 없습니다.');
          props?.actChage('list');
        }
      } catch (error) {
        console.error('게시글 상세 조회 오류:', error);
        alert('게시글을 불러오는 중 오류가 발생했습니다.');
        props?.actChage('list');
      } finally {
        setLoading(false);
      }
    };
    
    getBoardDetail();
  }, [searchParams.get('id'), api]);
  
  if (loading) {
    return (
      <div className={`section_wrap sec_wrap_2`}>
        <div className={`sec sec_2`}>
          <div className="text-center py-4">
            <p>게시글을 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!boardData) {
    return (
      <div className={`section_wrap sec_wrap_2`}>
        <div className={`sec sec_2`}>
          <div className="text-center py-4">
            <p>게시글을 찾을 수 없습니다.</p>
            <button 
              className="btn btn-primary mt-4"
              onClick={() => props?.actChage('list')}
            >
              목록으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className={`section_wrap sec_wrap_2`}>
        <div className={`sec sec_2`}>
          {/* 뒤로가기 버튼 및 액션 버튼들 */}
          <div className={`btn_group mb-4 flex justify-between items-center`}>
            <button 
              className={`btn btn-outline-secondary`}
              onClick={() => props?.actChage('list')}
            >
              ← 목록으로 돌아가기
            </button>
            
            <div className={`action_buttons flex gap-2`}>
              {/* 답변이 있는 경우 답변 보기 링크 */}
              {boardData.replies && boardData.replies.length > 0 && (
                <button 
                  className={`btn btn-outline-primary`}
                  onClick={() => {
                    // 답변 섹션으로 스크롤
                    const replySection = document.getElementById('admin-reply-section');
                    if (replySection) {
                      replySection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  답변 보기
                </button>
              )}
              
              {/* 본인이 작성한 글인 경우 삭제 버튼 */}
              {boardData.user_id === props?.userInfo?.id && (
                <button 
                  className={`btn btn-outline-danger`}
                  onClick={() => {
                    if (window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) {
                      handleDeleteBoard();
                    }
                  }}
                >
                  글 삭제
                </button>
              )}
            </div>
          </div>
          
          {/* 게시글 상세 내용 */}
          <div className={`board_detail`}>
            {/* 제목 */}
            <div className={`detail_header mb-4`}>
              <h2 className={`h2 mb-2`}>{boardData.subject}</h2>
              <div className={`detail_meta text-gray-600`}>
                <span className={`mr-4`}>카테고리: {boardData.category}</span>
                <span>작성일: {new Date(boardData.created_at).toLocaleDateString('ko-KR')}</span>
              </div>
            </div>
            
            {/* 내용 */}
            <div className={`detail_content mb-6`}>
              <div className={`content_box p-4 bg-gray-50 rounded`}>
                <p className={`body1 whitespace-pre-wrap`}>{boardData.description}</p>
              </div>
            </div>
            
            {/* 첨부 이미지 */}
            {boardData.images && boardData.images.length > 0 && (
              <div className={`detail_images mb-6`}>
                <h3 className={`h3 mb-3`}>첨부 이미지</h3>
                <div className={`image_grid grid grid-cols-2 md:grid-cols-3 gap-4`}>
                  {boardData.images.map((image: any, index: number) => (
                    <div key={index} className={`image_item`}>
                      {typeof image === 'string' ? (
                        <img 
                          src={image} 
                          alt={`첨부 이미지 ${index + 1}`}
                          className={`w-full h-32 object-cover rounded border`}
                        />
                      ) : image.image_url && typeof image.image_url === 'string' ? (
                        <img 
                          src={image.image_url} 
                          alt={image.original_filename || `첨부 이미지 ${index + 1}`}
                          className={`w-full h-32 object-cover rounded border`}
                        />
                      ) : (
                        <div className={`w-full h-32 bg-gray-200 rounded border flex items-center justify-center`}>
                          <span className={`text-gray-500 text-sm`}>이미지 로드 실패</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* 답변 상태 */}
            <div className={`detail_status mb-4`}>
              <div className={`status_badge inline-block px-3 py-1 rounded text-sm ${
                boardData.status === '답변완료' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {boardData.status === '답변완료' ? '답변완료' : '답변대기'}
              </div>
            </div>
            
            {/* 관리자 답변 */}
            {boardData.replies && boardData.replies.length > 0 && (
              <div className={`admin_reply`} id="admin-reply-section">
                <h3 className={`h3 mb-3`}>관리자 답변</h3>
                <div className={`reply_box p-4 bg-blue-50 rounded`}>
                  <p className={`body1 mb-2`}>{boardData.replies[0].content}</p>
                  <div className={`reply_date text-sm text-gray-600`}>
                    답변일: {new Date(boardData.replies[0].created_at).toLocaleDateString('ko-KR')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
export default View